import React, { useState } from 'react';
import { Church, LogIn, PlayCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

// Shared, pre-seeded demo identity (role=viewer, is_staff=false: browse only).
// Seeded server-side by scripts/seed_demo.py during deploy. These creds are
// deliberately public — this button hands them to anyone who clicks it.
const DEMO_CREDENTIALS = {
  email: 'demo@aichurchflow.aibuell.com',
  password: 'demo',
};

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      // navigation handled by App route guard on auth state change
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // One-click demo: log into the shared pre-seeded demo account.
  const handleDemo = async () => {
    setError('');
    setLoading(true);
    try {
      await login(DEMO_CREDENTIALS.email, DEMO_CREDENTIALS.password);
    } catch (err) {
      setError(err.message || 'Could not start the demo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="bg-brand-600 text-white rounded-2xl p-3 mb-3">
            <Church size={32} />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Church Workflow</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Church Administration</p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 space-y-4"
        >
          {error && (
            <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2 border border-red-200">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
              Email
            </label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="admin@church.local"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white font-medium rounded-lg px-4 py-2 flex items-center justify-center gap-2"
          >
            <LogIn size={18} />
            {loading ? 'Signing in…' : 'Sign in'}
          </button>

          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span className="flex-1 h-px bg-slate-200" />
            or
            <span className="flex-1 h-px bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={handleDemo}
            disabled={loading}
            className="w-full border border-brand-200 text-brand-700 hover:bg-brand-50 disabled:opacity-60 font-medium rounded-lg px-4 py-2 flex items-center justify-center gap-2"
          >
            <PlayCircle size={18} />
            Try a demo (no sign-up)
          </button>
          <p className="text-xs text-slate-400 text-center">
            Read-only tour of a sample church with example members &amp; households.
          </p>
        </form>
      </div>
    </div>
  );
}
